import {
  ListSystemDesignAlertsResponse,
  ListSystemDesignAlertsResponse_Alert,
  ListSystemDesignAlertsResponse_Alert_Field,
  ListSystemDesignAlertsResponse_Alert_Type,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { TFunction } from 'i18next';
import {
  createRelativeDrainRoute,
  createRelativeProductRoute,
  createRelativeReservoirRoute,
} from '~/shared/models/create-routes';
import { SystemModel } from '~/shared/models/system/SystemModel';
import { assertUnreachable } from '~/types/assert-type';

export type SystemDesignAlertSeverity = 'success' | 'warning' | 'error';

export interface SystemDesignAlert {
  text: string;
  isBlocking: boolean;
  route: string | null;
}

export class SystemDesignAlertsModel {
  private readonly alerts: ListSystemDesignAlertsResponse_Alert[];

  private readonly alertsByTarget: Map<
    string,
    ListSystemDesignAlertsResponse_Alert[]
  >;

  readonly shouldBlockSystemStateChange: boolean;

  readonly severity: SystemDesignAlertSeverity;

  constructor(systemDesignAlerts: ListSystemDesignAlertsResponse) {
    this.alerts = systemDesignAlerts.alerts.sort((a, b) =>
      b.type.toString().localeCompare(a.type.toString()),
    );

    this.alertsByTarget = this.buildAlertsByTargetMap();

    this.shouldBlockSystemStateChange = this.alerts.some(
      (alert) =>
        alert.type === ListSystemDesignAlertsResponse_Alert_Type.BLOCKING,
    );

    this.severity = this.initializeSeverity();
  }

  getLabel(t: TFunction, system: SystemModel): string {
    const allAlerts = this.getAllAlerts(t, system);
    const hasAlerts = allAlerts.length > 0;

    if (system.state === 'READY_FOR_INSTALL') {
      return hasAlerts
        ? t('systemDesignChecks.commissioningAlertsFound')
        : t('systemDesignChecks.commissioningAllGood');
    }

    return hasAlerts
      ? t('systemDesignChecks.designAlertsFound')
      : t('systemDesignChecks.designAllGood');
  }

  static getTitle(t: TFunction, system: SystemModel): string {
    if (system.state === 'READY_FOR_INSTALL') {
      return t('systemDesignChecks.commissioningTitle');
    }
    return t('systemDesignChecks.designTitle');
  }

  getAllAlerts(t: TFunction, system?: SystemModel): SystemDesignAlert[] {
    return this.alerts.map((alert) =>
      SystemDesignAlertsModel.mapAlert(alert, t, system),
    );
  }

  getAlertsForTarget = (target: string, t: TFunction): SystemDesignAlert[] =>
    this.alertsByTarget
      .get(target)
      ?.map((alert) => SystemDesignAlertsModel.mapAlert(alert, t)) || [];

  getTargetAlertSeverity = (target: string): SystemDesignAlertSeverity => {
    const alertsForTarget = this.alertsByTarget.get(target);

    if (!alertsForTarget?.length) {
      return 'success';
    }

    if (
      alertsForTarget.some(
        (alert) =>
          alert.type === ListSystemDesignAlertsResponse_Alert_Type.BLOCKING,
      )
    ) {
      return 'error';
    }

    return 'warning';
  };

  private buildAlertsByTargetMap = () => {
    const alertsMap = new Map<string, ListSystemDesignAlertsResponse_Alert[]>();

    this.alerts.forEach((alert) => {
      const { target } = alert;

      if (!alertsMap.has(target)) {
        alertsMap.set(target, []);
      }
      alertsMap.get(target)!.push(alert);
    });

    return alertsMap;
  };

  private static mapResourceToRoute(
    resourceName: string,
    system?: SystemModel,
  ) {
    const resourceParts = resourceName.split('/');
    if (resourceParts.length < 2) {
      throw new Error(`Invalid resource name: ${resourceName}`);
    }

    const resourceType = resourceParts[resourceParts.length - 2];
    const resourceId = resourceParts[resourceParts.length - 1];

    switch (resourceType) {
      case 'drains':
        return createRelativeDrainRoute(resourceId);
      case 'reservoirs':
        return createRelativeReservoirRoute(resourceId);
      case 'products':
        return createRelativeProductRoute(resourceId);
      case 'devices':
        if (system) {
          const product = system.productsByDeviceName(resourceName)[0];
          return createRelativeProductRoute(product.id);
        }
        return null;
      default:
        return null;
    }
  }

  private static mapAlert(
    alert: ListSystemDesignAlertsResponse_Alert,
    t: TFunction,
    system?: SystemModel,
  ): SystemDesignAlert {
    const isBlocking =
      alert.type === ListSystemDesignAlertsResponse_Alert_Type.BLOCKING;

    const route = SystemDesignAlertsModel.mapResourceToRoute(
      alert.target,
      system,
    );

    switch (alert.field) {
      case ListSystemDesignAlertsResponse_Alert_Field.INVALID_DRAIN_CONFIG:
        return {
          text: t('systemDesignChecks.invalidDrainConfig', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.CRATE_TYPE_NOT_SET:
        return {
          text: t('systemDesignChecks.crateTypeNotSet', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.POLDERVALVE_COUNT_LOW:
        return {
          text: t('systemDesignChecks.polderValveCountLow', {
            minExpected:
              alert.error?.type.case === 'tooLow'
                ? alert.error.type.value.min
                : 'N/A',
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.VALVE_INVALID_DRAIN_TYPE:
        return {
          text: t('systemDesignChecks.polderValveInvalidDrainType', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.NO_DIRECT_DRAIN:
        return {
          text: t('systemDesignChecks.noDirectDrain', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.UNLINKED_TAP:
        return {
          text: t('systemDesignChecks.unlinkedTap', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.UNLINKED_RAIN:
        return {
          text: t('systemDesignChecks.unlinkedRain', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.UNLINKED_VALVE:
        return {
          text: t('systemDesignChecks.unlinkedValve', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.UNLINKED_PUMP:
        return {
          text: t('systemDesignChecks.unlinkedPump', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.FLOATING_RESERVOIR:
        return {
          text: t('systemDesignChecks.floatingReservoir', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.NO_RESERVOIRS_EXISTS:
        return {
          text: t('systemDesignChecks.noReservoirs'),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.NO_PRODUCTS_EXISTS:
        return {
          text: t('systemDesignChecks.noProducts'),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.NOT_COMMISSIONED_DEVICE:
        return {
          text: t('systemDesignChecks.uncommissionedDevice', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.RAIN_MISSING:
        return {
          text: t('systemDesignChecks.rainMissing'),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.NO_SMART_DEVICES:
        return {
          text: t('systemDesignChecks.noSmartDevices'),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.OVERFLOW_DRAIN_HEIGHT:
        return {
          text: t('systemDesignChecks.overflowDrainHeight', {
            minHeight:
              alert.error?.type.case === 'tooLow'
                ? alert.error.type.value.min
                : 'N/A',
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.OVERFLOW_DRAIN_MISSING:
        return {
          text: t('systemDesignChecks.overflowDrainMissing', {
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.INFILTRATION_SPEED:
        if (alert.error?.type.case !== 'tooLow') {
          throw new Error(
            `Wrong error type for alert: ${JSON.stringify(alert)}`,
          );
        }
        return {
          text: t('systemDesignChecks.infiltrationSpeed', {
            minSpeed: SystemDesignAlertsModel.ceilToOneDecimal(
              alert.error.type.value.min,
            ),
            name: alert.targetTitle,
          }),
          isBlocking,
          route,
        };

      case ListSystemDesignAlertsResponse_Alert_Field.UNSPECIFIED:
        throw new Error('Unspecified alert field');

      default:
        return assertUnreachable(alert.field);
    }
  }

  private static ceilToOneDecimal(value: number): number {
    return Math.ceil(value * 10) / 10;
  }

  private initializeSeverity(): SystemDesignAlertSeverity {
    if (this.alerts.length === 0) {
      return 'success';
    }
    if (this.shouldBlockSystemStateChange) {
      return 'error';
    }
    return 'warning';
  }
}
